import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Package, Truck, Clock, CheckCircle, Plus, RefreshCw, Search,
  Bed, Accessibility, Wind, Bath, ArrowUp, Box, Eye, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DMEManagement = () => {
  const [catalog, setCatalog] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showNewOrder, setShowNewOrder] = useState(false);
  
  const [newOrder, setNewOrder] = useState({
    patient_id: '',
    patient_name: '',
    items: [],
    vendor_name: 'MedEquip Solutions',
    priority: 'standard',
    delivery_date: '',
    notes: ''
  });

  const [selectedItems, setSelectedItems] = useState([]);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCatalog(),
        loadCategories(),
        loadOrders(),
        loadVendors(),
        loadDashboard()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    setLoading(false);
  };

  const loadCatalog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/catalog`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCatalog(data.equipment || []);
      }
    } catch (error) {
      console.error('Error loading catalog:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/categories`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data || []);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadOrders = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/orders`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setOrders(data.orders || []);
      }
    } catch (error) {
      console.error('Error loading orders:', error);
    }
  };

  const loadVendors = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/vendors`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setVendors(data.vendors || []);
      }
    } catch (error) {
      console.error('Error loading vendors:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/dashboard`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDashboard(data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  const addItemToOrder = (item) => {
    const existing = selectedItems.find(i => i.id === item.id);
    if (existing) {
      toast.warning('Item already added');
      return;
    }
    setSelectedItems([...selectedItems, { ...item, quantity: 1, type: 'rental', rental_days: 30 }]);
    toast.success(`Added ${item.name}`);
  };

  const removeItemFromOrder = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const createOrder = async () => {
    if (!newOrder.patient_id || selectedItems.length === 0) {
      toast.error('Please enter patient ID and select items');
      return;
    }

    const orderPayload = {
      ...newOrder,
      items: selectedItems.map(item => ({
        equipment_id: item.id,
        equipment_name: item.name,
        quantity: item.quantity,
        type: item.type,
        rental_days: item.rental_days,
        rental_daily: item.rental_daily,
        purchase_price: item.purchase_price
      }))
    };

    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(orderPayload)
      });

      if (response.ok) {
        toast.success('Order created successfully');
        setShowNewOrder(false);
        setSelectedItems([]);
        setNewOrder({
          patient_id: '',
          patient_name: '',
          items: [],
          vendor_name: 'MedEquip Solutions',
          priority: 'standard',
          delivery_date: '',
          notes: ''
        });
        loadOrders();
        loadDashboard();
      }
    } catch (error) {
      toast.error('Failed to create order');
    }
  };

  const updateOrderStatus = async (orderId, status) => {
    try {
      const response = await fetch(`${API_URL}/api/phase31/dme/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ status })
      });

      if (response.ok) {
        toast.success(`Order ${status}`);
        loadOrders();
        loadDashboard();
      }
    } catch (error) {
      toast.error('Failed to update order');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      ordered: 'bg-purple-100 text-purple-800',
      shipped: 'bg-orange-100 text-orange-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || styles.pending}>{status.toUpperCase()}</Badge>;
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'mobility': return <Accessibility className="h-5 w-5" />;
      case 'beds': return <Bed className="h-5 w-5" />;
      case 'respiratory': return <Wind className="h-5 w-5" />;
      case 'bathroom': return <Bath className="h-5 w-5" />;
      case 'lifts': return <ArrowUp className="h-5 w-5" />;
      default: return <Box className="h-5 w-5" />;
    }
  };

  const filteredCatalog = selectedCategory === 'all' 
    ? catalog 
    : catalog.filter(item => item.category === selectedCategory);

  const formatCurrency = (amount) => {
    return amount ? `$${amount.toFixed(2)}` : 'N/A';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="dme-loading">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dme-management">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Package className="h-7 w-7 text-teal-600" />
            DME Management
          </h1>
          <p className="text-gray-500">Durable Medical Equipment ordering and tracking</p>
        </div>
        <Dialog open={showNewOrder} onOpenChange={setShowNewOrder}>
          <DialogTrigger asChild>
            <Button className="bg-teal-600 hover:bg-teal-700" data-testid="new-order-btn">
              <Plus className="h-4 w-4 mr-2" />
              New Order
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create Equipment Order</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Patient ID *</Label>
                  <Input
                    value={newOrder.patient_id}
                    onChange={(e) => setNewOrder({...newOrder, patient_id: e.target.value})}
                  />
                </div>
                <div>
                  <Label>Patient Name</Label>
                  <Input
                    value={newOrder.patient_name}
                    onChange={(e) => setNewOrder({...newOrder, patient_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vendor</Label>
                  <Select value={newOrder.vendor_name} onValueChange={(v) => setNewOrder({...newOrder, vendor_name: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {vendors.map(v => (
                        <SelectItem key={v.id} value={v.name}>{v.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select value={newOrder.priority} onValueChange={(v) => setNewOrder({...newOrder, priority: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="stat">STAT (Same Day)</SelectItem>
                      <SelectItem value="urgent">Urgent (24hrs)</SelectItem>
                      <SelectItem value="standard">Standard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Selected Items */}
              <div>
                <Label>Selected Items ({selectedItems.length})</Label>
                {selectedItems.length === 0 ? (
                  <p className="text-sm text-gray-500 py-2">No items selected. Browse catalog below.</p>
                ) : (
                  <div className="space-y-2 mt-2">
                    {selectedItems.map(item => (
                      <div key={item.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                        <span className="text-sm">{item.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-gray-500">
                            {item.type === 'purchase' ? formatCurrency(item.purchase_price) : `${formatCurrency(item.rental_daily)}/day`}
                          </span>
                          <Button size="sm" variant="ghost" onClick={() => removeItemFromOrder(item.id)}>
                            <XCircle className="h-4 w-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Quick Add from Catalog */}
              <div>
                <Label>Quick Add Equipment</Label>
                <div className="grid grid-cols-2 gap-2 mt-2 max-h-48 overflow-y-auto">
                  {catalog.slice(0, 10).map(item => (
                    <Button
                      key={item.id}
                      variant="outline"
                      size="sm"
                      className="justify-start text-left"
                      onClick={() => addItemToOrder(item)}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {item.name}
                    </Button>
                  ))}
                </div>
              </div>

              <Button className="w-full" onClick={createOrder}>Create Order</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboard Stats */}
      {dashboard && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Orders</p>
                  <p className="text-2xl font-bold">{dashboard.pending_orders}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">In Transit</p>
                  <p className="text-2xl font-bold">{dashboard.in_transit}</p>
                </div>
                <Truck className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Delivered MTD</p>
                  <p className="text-2xl font-bold">{dashboard.delivered_mtd}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Equipment</p>
                  <p className="text-2xl font-bold">{dashboard.total_active_equipment}</p>
                </div>
                <Package className="h-8 w-8 text-teal-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="dashboard">Orders</TabsTrigger>
          <TabsTrigger value="catalog">Equipment Catalog</TabsTrigger>
          <TabsTrigger value="vendors">Vendors</TabsTrigger>
        </TabsList>

        {/* Orders Tab */}
        <TabsContent value="dashboard" className="mt-4">
          {orders.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Package className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No orders yet</p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {orders.map((order) => (
                <Card key={order.id}>
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-medium">{order.id}</span>
                          {getStatusBadge(order.status)}
                          <Badge variant="outline" className="capitalize">{order.priority}</Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {order.patient_name || order.patient_id} • {order.items?.length || 0} items
                        </p>
                        <p className="text-xs text-gray-400">
                          Vendor: {order.vendor_name} • Total: {formatCurrency(order.total_cost)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {order.status === 'pending' && (
                          <Button size="sm" onClick={() => updateOrderStatus(order.id, 'approved')}>
                            Approve
                          </Button>
                        )}
                        {order.status === 'approved' && (
                          <Button size="sm" onClick={() => updateOrderStatus(order.id, 'shipped')}>
                            Mark Shipped
                          </Button>
                        )}
                        {order.status === 'shipped' && (
                          <Button size="sm" className="bg-green-600" onClick={() => updateOrderStatus(order.id, 'delivered')}>
                            Mark Delivered
                          </Button>
                        )}
                        <Button size="sm" variant="outline">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Catalog Tab */}
        <TabsContent value="catalog" className="mt-4">
          <div className="flex gap-4 mb-4 flex-wrap">
            <Button
              variant={selectedCategory === 'all' ? 'default' : 'outline'}
              onClick={() => setSelectedCategory('all')}
            >
              All
            </Button>
            {categories.map(cat => (
              <Button
                key={cat.id}
                variant={selectedCategory === cat.id ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(cat.id)}
              >
                {getCategoryIcon(cat.id)}
                <span className="ml-2">{cat.name}</span>
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCatalog.map(item => (
              <Card key={item.id}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-gray-100 rounded">
                      {getCategoryIcon(item.category)}
                    </div>
                    <div className="flex-1">
                      <h4 className="font-medium">{item.name}</h4>
                      <p className="text-sm text-gray-500">{item.description}</p>
                      <div className="flex gap-2 mt-2 text-xs">
                        <span>Rent: {formatCurrency(item.rental_daily)}/day</span>
                        {item.purchase_price && <span>• Buy: {formatCurrency(item.purchase_price)}</span>}
                      </div>
                      {item.requires_auth && (
                        <Badge variant="outline" className="mt-2 text-xs">Prior Auth Required</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Vendors Tab */}
        <TabsContent value="vendors" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {vendors.map(vendor => (
              <Card key={vendor.id}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{vendor.name}</h4>
                      <p className="text-sm text-gray-500 capitalize">{vendor.type.replace('_', ' ')}</p>
                      <p className="text-sm text-gray-600 mt-2">{vendor.phone}</p>
                      <p className="text-sm text-gray-600">{vendor.email}</p>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {vendor.services?.map((service, idx) => (
                          <Badge key={idx} variant="outline" className="text-xs capitalize">
                            {service.replace('_', ' ')}
                          </Badge>
                        ))}
                      </div>
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      {vendor.rating} ★
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default DMEManagement;
